import { z } from '../zod.js';
import { DashboardUrlField } from '../shared/dashboardUrl';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import { PaginatedListSchema } from '../shared/paginatedList';
import { AttendeeDetailsResponseShape } from '../group-registration/attendeeResponse';
import { beta } from '../beta';
import {
  TicketCheckInMethodValidationEnum,
  TicketCheckInStatusValidationEnum,
  TicketPricingValidationEnum,
  TicketStatusValidationEnum,
} from './enum';

export const TicketCheckInValidationSchema = z.object({
  status: TicketCheckInStatusValidationEnum.meta({
    description:
      'Check-in state recorded by this scan. `CHECKEDIN` admitted the guest, `UNCHECKEDIN` reversed an earlier check-in.',
  }),
  method: TicketCheckInMethodValidationEnum.meta({
    description:
      'How the check-in happened. `QRSCAN` is the ticket QR code scanned at the door, `MANUAL` is door staff tapping the check-in button.',
  }),
  checkedInBy: ObjectIdStringValidationSchema.meta({
    description: 'Id of the user (event staff or seller) who performed the check-in.',
  }),
  checkedInAt: z.iso.datetime().meta({
    description: 'ISO-8601 UTC timestamp the check-in was recorded.',
  }),
});

export const TicketMetadataEntryValidationSchema = z.object({
  key: z.string().meta({
    description: 'Metadata key. Maximum 50 characters.',
  }),
  value: z.string().meta({
    description: 'Metadata value. Maximum 100 characters.',
  }),
  addedAt: z.iso.datetime().meta({
    description: 'ISO-8601 UTC timestamp the entry was added.',
  }),
});

export const TicketResponseValidationSchema = z
  .object({
    id: ObjectIdStringValidationSchema.meta({
      description: 'Unique identifier for the ticket.',
    }),
    seller: ObjectIdStringValidationSchema.meta({
      deprecated: true,
      description: 'Deprecated alias of `sellerId`. Use `sellerId` instead.',
    }),
    sellerId: ObjectIdStringValidationSchema.meta({
      description: 'Id of the seller account the ticket belongs to.',
    }),
    charge: ObjectIdStringValidationSchema.meta({
      deprecated: true,
      description: 'Deprecated alias of `bookingId`. Use `bookingId` instead.',
    }),
    bookingId: ObjectIdStringValidationSchema.meta({
      description:
        'Id of the booking (Charge) this ticket was created from. A booking of three tickets produces three ticket records sharing one `bookingId`. Retrieve it with the bookings endpoint.',
    }),
    orderId: z.string().meta({
      description:
        'Human-readable order reference shared by every ticket in the same booking. Shown to the customer on receipts and tickets.',
    }),
    page: ObjectIdStringValidationSchema.meta({
      deprecated: true,
      description: 'Deprecated alias of `pageId`. Use `pageId` instead.',
    }),
    pageId: ObjectIdStringValidationSchema.meta({
      description: 'Id of the event page the ticket was purchased from.',
    }),
    ticketType: ObjectIdStringValidationSchema.meta({
      deprecated: true,
      description: 'Deprecated alias of `ticketTypeId`. Use `ticketTypeId` instead.',
    }),
    ticketTypeId: ObjectIdStringValidationSchema.meta({
      description:
        'Id of the ticket type purchased (e.g. General Admission, VIP). Ticket types belong to a ticket group on the event.',
    }),
    originalPrice: z.number().meta({
      description:
        'Price of the ticket before any discounts, coupons, taxes or fees, in the smallest currency unit (e.g. cents).',
    }),
    pricing: TicketPricingValidationEnum.optional().meta({
      description:
        'Pricing model of the ticket type at the time of purchase. `paid` is a fixed price, `pwyw` means the buyer chose the amount, `free` is a zero-price ticket. Absent on tickets created before this field existed.',
    }),
    discountAmount: z.number().meta({
      description:
        'Bulk discount allocated to this ticket, in the smallest currency unit. Booking-level discounts are apportioned across the tickets in the booking.',
    }),
    feeAmount: z.number().meta({
      description:
        'Booking fees allocated to this ticket, in the smallest currency unit. Booking-level fees are apportioned across the tickets in the booking.',
    }),
    taxAmount: z.number().meta({
      description:
        'Tax allocated to this ticket, in the smallest currency unit. Booking-level tax is apportioned across the tickets in the booking.',
    }),
    couponAmount: z.number().meta({
      description:
        'Coupon discount allocated to this ticket, in the smallest currency unit. Booking-level coupon discounts are apportioned across the tickets in the booking.',
    }),
    complimentaryAmount: z.number().nullish().meta({
      description:
        'Face value and booking fee forgone on a complimentary booking, in the smallest currency unit. 0 otherwise.',
    }),
    revenue: z.number().meta({
      description:
        'Revenue attributed to this ticket, in the smallest currency unit: `originalPrice - discountAmount - couponAmount - complimentaryAmount + taxAmount + feeAmount`. Use this rather than summing the parts yourself.',
    }),
    currency: z.string().optional().meta({
      description: 'Lowercase ISO 4217 currency code for every amount on the ticket (e.g. "usd").',
    }),
    customer: ObjectIdStringValidationSchema.meta({
      deprecated: true,
      description: 'Deprecated alias of `customerId`. Use `customerId` instead.',
    }),
    customerId: ObjectIdStringValidationSchema.meta({
      description: 'Id of the customer who booked the ticket.',
    }),
    ticketShortId: z.string().meta({
      description:
        'Short uppercase alphanumeric code identifying the ticket (e.g. "A1B2C3D4"). Printed on the ticket and quoted by guests at the door — search on it to find a specific attendee.',
    }),
    checkInCode: z.string().meta({
      description:
        'Check-in credential for this ticket. Encode it as a QR code for the Checkout Page check-in scanner, or post it to the ticket validation endpoint, to admit the guest. Treat the value as opaque - its format may change.',
    }),
    status: TicketStatusValidationEnum.meta({
      description:
        'Lifecycle state of the ticket. `PAID` is valid for admission, `CANCELED` was canceled by the seller and must not be admitted. A refund alone never cancels a ticket.',
    }),
    checkIns: z.array(TicketCheckInValidationSchema).meta({
      description:
        'Full check-in history, oldest first. Empty when the guest has never been checked in. A guest checked in and back out again has multiple entries.',
    }),
    latestCheckIn: TicketCheckInValidationSchema.optional().meta({
      description:
        'Most recent entry from `checkIns`, denormalised for filtering. Absent when the guest has never been checked in — treat that as not checked in.',
    }),
    customerName: z.string().optional().meta({
      deprecated: true,
      description: 'Deprecated alias of `attendeeName`. Use `attendeeName` instead.',
    }),
    attendeeName: z.string().optional().meta({
      description:
        'Attendee name held on the ticket, shown on the ticket PDF and the guest list. Set from the purchaser at booking time; editable via the ticket update endpoint.',
    }),
    customerEmail: z.string().meta({
      deprecated: true,
      description: 'Deprecated alias of `attendeeEmail`. Use `attendeeEmail` instead.',
    }),
    attendeeEmail: z.string().meta({
      description:
        'Attendee email held on the ticket. The ticket is delivered here. Set from the purchaser at booking time; editable via the ticket update endpoint.',
    }),
    attendee: beta(
      z.object(AttendeeDetailsResponseShape).optional().meta({
        description:
          'The attendee this ticket is for, with their answers, on events that collect attendee details. Absent until the booker or seller adds them. `attendeeName` and `attendeeEmail` follow it.',
      }),
    ),
    livemode: z.boolean().meta({
      description:
        'Whether the ticket came from a live payment. Test-mode tickets are never returned by the seller API.',
    }),
    orderedAt: z.iso.datetime().meta({
      description:
        'ISO-8601 UTC timestamp the booking was placed. Matches across every ticket in the same booking.',
    }),
    canceledAt: z.iso.datetime().optional().meta({
      description:
        'ISO-8601 UTC timestamp the ticket was canceled. Absent while the ticket is valid.',
    }),
    cancelReason: z.string().optional().meta({
      description:
        'Why the seller canceled this ticket. Set only when the ticket was canceled on its own: when the whole booking is canceled, the reason is kept on the booking.',
    }),
    canceledBy: z.string().optional().meta({
      description:
        'ID of the dashboard user who canceled this ticket. Set only when the ticket was canceled on its own.',
    }),
    metadata: z.array(TicketMetadataEntryValidationSchema).meta({
      description:
        'Custom key/value entries attached to the ticket, typically written by door staff during check-in. Maximum 20 entries.',
    }),
    createdAt: z.iso.datetime().meta({
      description: 'ISO-8601 UTC timestamp the ticket record was created.',
    }),
    updatedAt: z.iso.datetime().meta({
      description: 'ISO-8601 UTC timestamp the ticket record was last updated.',
    }),
  })
  .meta({
    description:
      'A single admission ticket held by one attendee, created when an event booking is paid. Read-only.',
  });

export const GetTicketsResponseValidationSchema = PaginatedListSchema(
  TicketResponseValidationSchema,
);

export const TicketDetailResponseValidationSchema = z
  .object({
    data: TicketResponseValidationSchema,
  })
  .meta({ id: 'TicketDetailResponse', description: 'Envelope containing one ticket.' });

export const TicketToolDataValidationSchema = TicketResponseValidationSchema.extend({
  dashboardUrl: DashboardUrlField,
});

export const TicketDetailToolResponseValidationSchema = z.object({
  data: TicketToolDataValidationSchema,
});

export const TicketsToolResponseValidationSchema = PaginatedListSchema(
  TicketToolDataValidationSchema,
);
